import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import axios from 'axios';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useSettings } from '@/context/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { settings } = useSettings();
  const { hash } = useLocation();
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    interested_project: '',
    budget: '',
    bhk_type: '',
    source: '',
    referred_by: '',
    message: '',
  });
  const [customResponses, setCustomResponses] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const customFields = settings.enquiry_custom_fields || [];

  useEffect(() => {
    fetchProjects();
  }, []);

  // Scroll to enquiry form when arriving with #enquiry-form hash
  useEffect(() => {
    if (hash === '#enquiry-form') {
      const el = document.getElementById('enquiry-form');
      if (el) {
        // small delay so content/images settle before scrolling
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          const firstInput = el.querySelector('input, select, textarea');
          if (firstInput) firstInput.focus({ preventScroll: true });
        }, 200);
      }
    }
  }, [hash]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate: 10-digit phone; prefix +91 before submission
    const phoneDigits = (formData.phone || '').replace(/[^0-9]/g, '');
    if (phoneDigits.length !== 10) {
      toast.error('Please enter a valid 10-digit mobile number');
      return;
    }
    const fullPhone = `+91${phoneDigits}`;
    setSubmitting(true);

    try {
      const payload = { ...formData, phone: fullPhone, custom_responses: customResponses };
      await axios.post(`${API}/enquiries`, payload);
      toast.success('Thank you! We\'ll get back to you soon.');

      // Build WhatsApp message respecting visible fields + custom answers
      const parts = [`New Enquiry from Website!`, ``, `Name: ${formData.name}`, `Phone: ${fullPhone}`];
      if (settings.enquiry_show_email !== false && formData.email) parts.push(`Email: ${formData.email}`);
      if (settings.enquiry_show_interested_project !== false && formData.interested_project) parts.push(`Interested Project: ${formData.interested_project}`);
      if (settings.enquiry_show_budget !== false && formData.budget) parts.push(`Budget: ${formData.budget}`);
      if (settings.enquiry_show_bhk_type !== false && formData.bhk_type) parts.push(`BHK: ${formData.bhk_type}`);
      if (settings.enquiry_show_source !== false && formData.source) parts.push(`Source: ${formData.source}`);
      if (settings.enquiry_show_referred_by !== false && formData.referred_by) parts.push(`Referred By: ${formData.referred_by}`);
      Object.entries(customResponses).forEach(([k, v]) => { if (v) parts.push(`${k}: ${v}`); });
      if (settings.enquiry_show_message !== false && formData.message) parts.push(`Message: ${formData.message}`);

      const whatsappNumber = (settings.admin_whatsapp_number || settings.founders?.[0]?.whatsapp || '919511611575').replace(/[^0-9]/g, '');
      const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(parts.join('\n'))}`;
      window.open(whatsappUrl, '_blank');

      setFormData({
        name: '',
        phone: '',
        email: '',
        interested_project: '',
        budget: '',
        bhk_type: '',
        source: '',
        referred_by: '',
        message: '',
      });
      setCustomResponses({});
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error('Failed to submit enquiry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 text-center" data-testid="contact-heading">
            Get in Touch
          </h1>
          <p className="text-base sm:text-lg text-gray-600 text-center max-w-2xl mx-auto mb-12">
            Have questions? We're here to help. Reach out to us and we'll respond as soon as possible.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <div className="bg-white rounded-xl p-8 shadow-md mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="space-y-6">
                  {settings.show_phone_numbers !== false && settings.founders && settings.founders.length > 0 && (
                    settings.founders.map((founder, index) => (
                      <div key={index} className="flex items-start gap-4">
                        <div className="p-3 rounded-lg" style={{ background: `${settings.primary_color || '#C41E3A'}15` }}>
                          <Phone size={24} style={{ color: settings.primary_color || '#C41E3A' }} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 mb-1">Phone</p>
                          <p className="text-sm text-gray-600 mb-1">
                            {founder.name}
                            {founder.title ? ` · ${founder.title}` : ''}
                          </p>
                          {founder.qualification && (
                            <p className="text-xs text-gray-500 mb-1">{founder.qualification}</p>
                          )}
                          <a href={`tel:${founder.phone}`} className="text-gray-700 hover:text-red-700 font-medium">
                            {founder.phone}
                          </a>
                        </div>
                      </div>
                    ))
                  )}
                  {settings.show_email !== false && (
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg" style={{ background: `${settings.primary_color || '#C41E3A'}15` }}>
                        <Mail size={24} style={{ color: settings.primary_color || '#C41E3A' }} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 mb-1">Email</p>
                        <a href={`mailto:${settings.contact_email || 'info@infinitygroup.com'}`} className="text-gray-700 hover:text-red-700">
                          {settings.contact_email || 'info@infinitygroup.com'}
                        </a>
                      </div>
                    </div>
                  )}
                  {settings.show_address !== false && (
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-lg" style={{ background: `${settings.primary_color || '#C41E3A'}15` }}>
                        <MapPin size={24} style={{ color: settings.primary_color || '#C41E3A' }} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 mb-1">Office Address</p>
                        <a
                          href={
                            settings.contact_address_map_url ||
                            `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(settings.contact_address || 'Nagpur, Maharashtra, India')}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-600 hover:text-gray-900 hover:underline transition-colors inline-flex items-start gap-1"
                          data-testid="contact-address-link"
                        >
                          <span>{settings.contact_address || 'Nagpur, Maharashtra, India'}</span>
                        </a>
                        <p className="text-xs text-gray-400 mt-1">Click address to open in Google Maps</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div id="enquiry-form" className="bg-white rounded-xl p-8 shadow-md scroll-mt-24">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Enquiry Form</h2>
              <p className="text-sm text-gray-500 mb-6">
                Fill in your details and we'll get back to you on WhatsApp & email within 24 hours.
              </p>
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="enquiry-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      placeholder="Enter your name"
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-600 text-sm font-medium">
                        +91
                      </span>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={(e) => {
                          const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                          setFormData({ ...formData, phone: digits });
                        }}
                        required
                        pattern="[0-9]{10}"
                        maxLength={10}
                        inputMode="numeric"
                        className="flex-1 w-full px-4 py-3 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                        placeholder="98765 43210"
                        data-testid="input-phone"
                      />
                    </div>
                    {formData.phone && formData.phone.length !== 10 && (
                      <p className="text-xs text-red-600 mt-1">Enter a 10-digit mobile number</p>
                    )}
                  </div>
                </div>
                {settings.enquiry_show_email !== false && (
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      placeholder="your.email@example.com"
                      data-testid="input-email"
                    />
                  </div>
                )}
                {(settings.enquiry_show_interested_project !== false || settings.enquiry_show_bhk_type !== false) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {settings.enquiry_show_interested_project !== false && (
                  <div>
                    <label htmlFor="interested_project" className="block text-sm font-medium text-gray-700 mb-2">
                      Interested Property *
                    </label>
                    <select
                      id="interested_project"
                      name="interested_project"
                      value={formData.interested_project}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      data-testid="select-project"
                    >
                      <option value="">Select a property</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.name}>
                          {project.name} - {project.location_city}
                        </option>
                      ))}
                      <option value="General Enquiry">General Enquiry</option>
                    </select>
                  </div>
                  )}
                  {settings.enquiry_show_bhk_type !== false && (
                  <div>
                    <label htmlFor="bhk_type" className="block text-sm font-medium text-gray-700 mb-2">
                      BHK Type
                    </label>
                    <select
                      id="bhk_type"
                      name="bhk_type"
                      value={formData.bhk_type}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      data-testid="select-bhk"
                    >
                      <option value="">Select BHK</option>
                      <option value="1 BHK">1 BHK</option>
                      <option value="2 BHK">2 BHK</option>
                      <option value="3 BHK">3 BHK</option>
                      <option value="4 BHK">4 BHK</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  )}
                </div>
                )}
                {settings.enquiry_show_budget !== false && (
                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                    Budget Range
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    data-testid="select-budget"
                  >
                    <option value="">Select budget range</option>
                    <option value="Below 30 Lakhs">Below 30 Lakhs</option>
                    <option value="30-50 Lakhs">30-50 Lakhs</option>
                    <option value="50-75 Lakhs">50-75 Lakhs</option>
                    <option value="75 Lakhs - 1 Cr">75 Lakhs - 1 Cr</option>
                    <option value="1-2 Cr">1-2 Cr</option>
                    <option value="Above 2 Cr">Above 2 Cr</option>
                  </select>
                </div>
                )}
                {(settings.enquiry_show_source !== false || settings.enquiry_show_referred_by !== false) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {settings.enquiry_show_source !== false && (
                  <div>
                    <label htmlFor="source" className="block text-sm font-medium text-gray-700 mb-2">
                      How did you hear about us?
                    </label>
                    <input
                      type="text"
                      id="source"
                      name="source"
                      value={formData.source}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      placeholder="e.g., Google, Facebook, Friend"
                      data-testid="input-source"
                    />
                  </div>
                  )}
                  {settings.enquiry_show_referred_by !== false && (
                  <div>
                    <label htmlFor="referred_by" className="block text-sm font-medium text-gray-700 mb-2">
                      Referred By
                    </label>
                    <input
                      type="text"
                      id="referred_by"
                      name="referred_by"
                      value={formData.referred_by}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      placeholder="Name of person who referred you"
                      data-testid="input-referred"
                    />
                  </div>
                  )}
                </div>
                )}
                {/* Custom Fields */}
                {customFields.filter((f) => f.label).map((field, idx) => (
                  <div key={`cf-${idx}`} data-testid={`custom-field-${idx}`}>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {field.label}{field.required ? ' *' : ''}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        value={customResponses[field.label] || ''}
                        onChange={(e) => setCustomResponses({ ...customResponses, [field.label]: e.target.value })}
                        rows="3"
                        required={!!field.required}
                        placeholder={field.placeholder || ''}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      />
                    ) : (
                      <input
                        type="text"
                        value={customResponses[field.label] || ''}
                        onChange={(e) => setCustomResponses({ ...customResponses, [field.label]: e.target.value })}
                        required={!!field.required}
                        placeholder={field.placeholder || ''}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                      />
                    )}
                  </div>
                ))}
                {settings.enquiry_show_message !== false && (
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Message (Optional)
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="3"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="Tell us more about your requirements..."
                    data-testid="input-message"
                  ></textarea>
                </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  data-testid="submit-enquiry-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Enquiry'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;